import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import PlanGrid, { runLabel } from '../../components/common/PlanGrid';
import { apiErrorText, apiRequest, downloadApiFile, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyTournament.css';
import './HostEvent.css';

/* The multi-day run sheet — every day of the event on one screen.

   The card on the host screen edits one day at a time and one line at a time, each field carrying
   a whole date. That is right for a change made during the event and wrong for writing a
   three-day programme: the same date typed thirty times, and a tab switch to see what day two
   looks like.

   Here the DAY is the heading, so a line only carries what differs from the line above it — two
   hours and what happens. Every line is editable at once, adding one continues where the previous
   one ended, a day can be copied wholesale onto another, and one Save per day writes it.

   ★★ **And the competition is ON it, which is the whole point of this screen on a tournament
   day.** A run sheet that shows hours and titles and nothing else is unworkable the moment the
   event is a competition: "Game 3" says nothing about which round that is of how many, what is
   played in it, or which of these lines you may push around — so there is no way to tell where an
   item can go. Three things answer that, and all three come from the server:

       the PLAN        how many playing moments the format needs, and the computed day itself —
                       which this screen can write down in one press ("generate from how it will
                       look"), per day, in the organiser's own words.
       the PICTURE     the grid or the bracket, the same drawing the cockpit shows.
       the DRAW        once the schedule has been built: the real heats, under the line they hang
                       off. Group A plays Tug of War with A1 A2 A3 — at this hour, on this line.

   ★★ **The day FLOWS.** Slipping a keynote in between two games pushes everything after it along,
   and so does stretching one or taking one out. That is what makes a run sheet writable at all; it
   is also what makes the competition follow, because a playing moment carries its heats and its
   heats carry the clock every other screen reads (``sync_heat_times``). It can be switched off for
   the surgical edit of a single line during the event.

   ★ A slot has no day of its own; it has a START. Moving a line to another day means rewriting
   that start, which is why times go over the wire as a full 'YYYY-MM-DDTHH:MM' and never as a
   bare 'HH:MM': the server parses a bare hour against the EVENT's first day, and every line of
   day three would silently land on day one.

   ★ A slot whose name comes from the activity catalogue keeps that name — it belongs to the game
   and other events read it. Its hours are the host's to move; its title and its existence are
   not, which is the same rule the host card draws.

   ★ One Save per day is ONE write. A flowing day moves a dozen lines at once, and a dozen
   one-line writes is a dozen round trips and a half-written day the first time one is refused.
   The answer says which unsaved line became which row, so only the day that was saved is
   re-adopted — re-adopting everything would throw away what is typed on the other days. */

const HHMM = /^\d{2}:\d{2}$/;
// The same fortnight the server caps a run sheet's span at. A mistyped end date is a typo, not a
// request for a thousand day sections.
const MAX_DAYS = 14;
// What an item slipped in between two others is worth until somebody says otherwise. Long enough
// to be a real moment, short enough that the day it pushes along is still recognisable.
const INSERT_MINUTES = 15;
// A day ends at midnight. Pushing past it is refused rather than wrapped — a line that silently
// landed on tomorrow would be invisible on the day it was written for.
const DAY_END = 23 * 60 + 59;

// What a day is offered as when nobody has said otherwise. Both are drawn in editable boxes, so
// they are a starting point somebody can see and change — not a rule hiding in the code.
const DEFAULT_DAY_START = '09:00';
const DEFAULT_DAY_END = '18:00';

// Midday, never midnight: a bare ISO date parses as UTC, and 00:00 local is a different DAY for
// half the world.
const addDay = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const at = (day, hhmm) => (day && HHMM.test(hhmm || '') ? `${day}T${hhmm}` : '');

// An end before the start is the small hours, not a typo: a line running 23:00–01:00 ends on the
// next day, and composing it on this one is exactly what the server refuses.
const endAt = (day, start, end) => (
  HHMM.test(end || '') && HHMM.test(start || '') && end < start
    ? at(addDay(day), end) : at(day, end));

const stamp = (title, start, end, play) => `${title}|${start}|${end}|${play ? 1 : 0}`;

/* --- the clock, in minutes -----------------------------------------------------------------
   Every shift below is arithmetic on minutes since midnight and never on strings. 'HH:MM' + 45
   minutes is not a string operation, and a day that flows does it on every keystroke. */
const toMin = (hhmm) => (HHMM.test(hhmm || '')
  ? Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5)) : null);

const toHHMM = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const clamp = (minutes) => Math.max(0, Math.min(DAY_END, minutes));

/* One slot as a line of the editor. ``was`` is what it looked like on arrival, so Save writes the
   lines that actually moved and leaves the rest alone. */
const toRow = (slot, day) => ({
  key: `s${slot.id}`,
  id: slot.id,
  day,
  title: slot.title || '',
  start: (slot.start || '').slice(11, 16),
  end: (slot.end || '').slice(11, 16),
  is_activity: !!slot.is_activity,
  // ★★ Is the competition PLAYED in this moment? The one thing on a run-sheet line the schedule
  // generator reads. Everything else here is a programme; this is the part that becomes heats.
  is_challenge: !!slot.is_challenge,
  // Which playing moment it is. With two groups a PAIR of lines shares one number, because the
  // groups play it at the same time — so this is not the line's own position.
  sport_order: slot.sport_order,
  // Which group's turn this line is. Only ever visible on a multi-group day, where a playing
  // moment is one line per group and the pair share one moment number.
  group: slot.group || null,
  // The schedule has been built on this line — heats hang off it. Un-ticking or binning it takes
  // them with it. Its HOURS are the host's either way: moving the line moves the heats with it.
  is_generated: !!slot.is_generated,
  // ★★ Something has been SCORED here, and that is what closes the tick and the bin — the API
  // refuses them too. Never ``is_generated``: one press of Generate builds every day at once, so
  // that flag is true of a day nobody has played yet.
  is_played: !!slot.is_played,
  was: stamp(slot.title || '', slot.start || '', slot.end || '', slot.is_challenge),
});

const rowsOf = (planning, firstDay) => {
  const out = [];
  Object.entries(planning || {}).forEach(([day, slots]) => {
    // A slot with no start at all sits under '' — legacy rows from Machina. Show it on day one
    // with empty hours rather than hiding it: an unscheduled line is worth seeing.
    (slots || []).forEach((slot) => out.push(toRow(slot, day || firstDay)));
  });
  return out;
};

const HostRunSheet = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [gone, setGone] = useState([]);          // {id, day} removed here, deleted on save
  const [lastDay, setLastDay] = useState('');
  // ★ OFF here, unlike the event editor. Adding days is how a programme gets WRITTEN on this
  // screen — often several times over before it is right — and each save would otherwise mail
  // everybody who registered. Ticking it is the deliberate act, not leaving it alone.
  const [notify, setNotify] = useState(false);
  // ★★ ON, because writing a day is the reason this screen exists and a day that does not flow
  // means retyping every hour below the one that changed. Off is the surgical edit: during the
  // event, one line moves and nothing else may.
  const [flow, setFlow] = useState(true);
  const [shape, setShape] = useState(false);     // the plan and the picture, folded away
  const [busy, setBusy] = useState(false);
  // Where the message belongs as well as what it says: a page this tall must answer next to the
  // button that was pressed, not at the top where nobody is looking.
  const [notice, setNotice] = useState(null);    // {where, text}
  // Days the organiser added by hand — the non-consecutive case. Screen state, exactly as
  // Machina's Add Day is: a day becomes real by carrying a line.
  const [extraDays, setExtraDays] = useState([]);
  const [newDay, setNewDay] = useState('');
  // What each day is worth in hours, per day, so the plan can be spread over them. Also screen
  // state: once a day has been written down its lines ARE its window.
  const [windows, setWindows] = useState({});    // {day: {start, end}}
  // The split, as the server computes it from those windows. null until asked for.
  const [plan, setPlan] = useState(null);
  const seq = useRef(0);

  const event = (data && data.event) || {};
  const canManage = !!(data && data.can_manage);
  const firstDay = (event.date || '').slice(0, 10);
  // What the competition makes of this sheet — null on an ordinary event, and then this screen is
  // exactly the screen it always was.
  const comp = (data && data.competition) || null;

  const adopt = useCallback((res) => {
    setData(res);
    setRows(rowsOf(res.planning, ((res.event || {}).date || '').slice(0, 10)));
    setGone([]);
    setLastDay(((res.event || {}).end_date || '').slice(0, 10));
  }, []);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/planning`)
      .then(adopt)
      // Not the admin, not the host, or not this company's event — the host screen says so.
      // ★ REPLACE, never push: a refusal that pushes leaves this screen sitting in the history
      // behind the one it bounced to, so stepping back lands on it, it refuses again, and back
      // is a button that does nothing. Replacing takes the dead end out of the history with it.
      .catch(() => navigate(`/host-event/${eventId}`, { replace: true }));
  }, [eventId, navigate, adopt]);

  useEffect(() => { load(); }, [load]);

  /* --- the days ------------------------------------------------------------------------- */

  /* ★★ The days of this run sheet, and NONE of them is stored as a day.
     A line carries a full date, so a day exists because lines sit on it — which is exactly how
     Machina's own "Add Day" has always worked: it draws a date heading, and whatever gets typed
     under it becomes rows carrying that date. Nothing about the day itself is written down.

     Three sources, in this order:
       the event's own span   date .. end_date, the ordinary consecutive case
       any day carrying a line  so shortening an event does not hide yesterday's programme —
                                it shows it, so it can be moved
       days added here          the non-consecutive case: four Thursdays are four dates nobody
                                can reach by walking a span. Screen state until the day is saved
                                with something on it, and then it is simply one of the above. */
  const days = useMemo(() => {
    const out = [];
    const last = (event.end_date || '').slice(0, 10) || firstDay;
    let cursor = firstDay;
    while (cursor && cursor <= last && out.length < MAX_DAYS) {
      out.push(cursor);
      cursor = addDay(cursor);
    }
    rows.forEach((r) => { if (r.day && !out.includes(r.day)) out.push(r.day); });
    extraDays.forEach((d) => { if (d && !out.includes(d)) out.push(d); });
    return out.sort();
  }, [event.end_date, firstDay, rows, extraDays]);

  /* ★★ What a day is worth in hours. Defaults are SHOWN, never assumed: the organiser sees both
     boxes and changes them, which is the only honest way to have a default at all here.

     ⚠️ The end deliberately does NOT default to ``Event.expected_end_time``. That column is an
     OUTPUT — the server stamps it from the single-day sum — and feeding a derived value back in
     as an input is how two numbers start chasing each other. A plain working-day end, on screen,
     in a box. */
  const windowOf = useCallback((day) => windows[day]
    || { start: (comp && comp.day_start) || DEFAULT_DAY_START, end: DEFAULT_DAY_END },
  [windows, comp]);

  const setWindow = (day, patchWin) => setWindows((prev) => ({
    ...prev, [day]: { ...windowOf(day), ...patchWin },
  }));

  /* ★★ Ask the SERVER to spread the rounds over the days. Not computed here on purpose: the
     minutes of a day are ``run_sheet``'s sum term for term, and a second implementation in
     JavaScript is two answers to "when does this day end" that drift the first time somebody
     changes a rule. The days ride along with the question and are stored nowhere. */
  useEffect(() => {
    if (!comp || days.length < 2) { setPlan(null); return; }
    let alive = true;
    apiRequest('POST', `/company/event/${eventId}/tournament/preview`, {
      days: days.map((d) => ({ date: d, ...windowOf(d) })),
    })
      .then((res) => { if (alive) setPlan(res.preview || null); })
      .catch(() => { if (alive) setPlan(null); });
    return () => { alive = false; };
  }, [comp, days, eventId, windowOf]);

  // What the split made of one day — null on a single-day event, where there is nothing to split.
  const dayPlan = useCallback((day) => (plan && Array.isArray(plan.days)
    ? plan.days.find((d) => d.date === day) : null), [plan]);

  // The lines to write onto one day. With several days that is this day's SLICE of the
  // competition — day two gets rounds 7..12, not a second copy of day one.
  const planFor = useCallback((day) => {
    if (plan && Array.isArray(plan.days)) return (dayPlan(day) || {}).lines || [];
    return (comp && comp.lines) || [];
  }, [plan, dayPlan, comp]);

  // ★ In CLOCK order, not in the order the lines were typed or loaded. That is the order the day
  // is lived in, the order the server numbers the playing moments in, and — now that a line can be
  // slipped in between two others — the only order that puts a new line where it was put.
  const linesOf = useCallback((day) => rows
    .map((r, index) => ({ r, index }))
    .filter(({ r }) => r.day === day)
    .sort((a, b) => (a.r.start || '').localeCompare(b.r.start || '') || a.index - b.index)
    .map(({ r }) => r), [rows]);

  /* --- editing -------------------------------------------------------------------------- */

  const compose = (r) => ({
    title: (r.title || '').trim(),
    start: at(r.day, r.start),
    end: endAt(r.day, r.start, r.end),
    is_challenge: !!r.is_challenge,
  });

  /* Worth writing? An existing line, when it no longer looks like it did on arrival. A new one,
     when it has been given a name — a line added and left blank is an empty row on screen, not a
     save the server should be asked to refuse. */
  const isDirty = (r) => {
    const c = compose(r);
    return r.id ? stamp(c.title, c.start, c.end, c.is_challenge) !== r.was : !!c.title;
  };

  /* ★★ A line that LEFT this day still belongs to this day's Save. Without ``movedFrom`` the
     filters below stop seeing it the instant it moves — pressing Save on the day you are looking
     at would write nothing and the move would evaporate without a word. */
  const ofDay = (r, day) => r.day === day || r.movedFrom === day;

  const dayDirty = (day) => gone.some((g) => g.day === day)
    || rows.some((r) => ofDay(r, day) && isDirty(r));

  /* Moving a line to another day. The date part of its start is all that changes — a slot has no
     day of its own — and the heats follow the hours (``sync_heat_times``), so a generated moment
     travels with its games.

     ★ The flow does NOT fire on the destination: arriving somewhere is an explicit act, not a
     reason to push that morning along. The gap it leaves behind IS closed, exactly as removing a
     line closes it. */
  const moveLine = (row, day) => {
    if (!day || day === row.day) return;
    const from = toMin(row.start);
    const length = toMin(row.end) === null || from === null
      ? 0 : Math.max(0, toMin(row.end) - from);
    setRows((prev) => {
      const moved = prev.map((r) => (r.key === row.key
        ? { ...r, day, movedFrom: r.movedFrom || r.day } : r));
      return flow && from !== null
        ? shiftDay(moved, row.day, from, -length, row.key) : moved;
    });
  };

  const patch = (key, fields) => setRows((prev) =>
    prev.map((r) => (r.key === key ? { ...r, ...fields } : r)));

  /* ★★ The rest of the day moves along. Everything on this day that starts at or after ``from``
     is pushed by ``delta`` minutes — the line that was edited excepted, because it has already
     been given its own new hours.

     ★ Both hours of a pushed line move, so its LENGTH is untouched: a game that lasts half an
     hour lasts half an hour wherever the day puts it.

     ★ Clamped at midnight rather than wrapped. A line silently landing on tomorrow is a line
     nobody finds; a pile-up at 23:59 is visible, and the day says so underneath. */
  const shiftDay = (prev, day, from, delta, exceptKey) => (delta === 0 ? prev : prev.map((r) => {
    if (r.day !== day || r.key === exceptKey) return r;
    const start = toMin(r.start);
    if (start === null || start < from) return r;
    const end = toMin(r.end);
    return {
      ...r,
      start: toHHMM(clamp(start + delta)),
      end: end === null ? r.end : toHHMM(clamp(end + delta)),
    };
  }));

  // Does anything on this day sit against midnight? Then the flow has run out of day.
  const dayOverflows = (day) => linesOf(day).some((r) => toMin(r.start) >= DAY_END);

  /* Moving a line. With the day flowing it keeps its length and takes everything after it along;
     without, the end follows the start only while the two are equal — the same rule the host card
     draws, and the reason a line is typed by adjusting one hour. */
  const setStart = (row, start) => {
    const was = toMin(row.start);
    const now = toMin(start);
    if (!flow || was === null || now === null) {
      patch(row.key, { start, end: row.end === row.start ? start : row.end });
      return;
    }
    const length = toMin(row.end) === null ? 0 : Math.max(0, toMin(row.end) - was);
    setRows((prev) => shiftDay(
      prev.map((r) => (r.key === row.key
        ? { ...r, start, end: toHHMM(clamp(now + length)) } : r)),
      row.day, was, now - was, row.key));
  };

  /* Stretching or shortening a line. What follows it starts later or earlier by exactly that
     much — which is what "the competition planning follows" means on the clock. */
  const setEnd = (row, end) => {
    const was = toMin(row.end);
    const now = toMin(end);
    if (!flow || was === null || now === null || was < (toMin(row.start) || 0)) {
      patch(row.key, { end });
      return;
    }
    setRows((prev) => shiftDay(prev.map((r) => (r.key === row.key ? { ...r, end } : r)),
                               row.day, was, now - was, row.key));
  };

  const blankRow = (day, start, end) => {
    seq.current += 1;
    return { key: `n${seq.current}`, id: null, day, title: '', start, end,
             is_activity: false, is_challenge: false, is_generated: false, is_played: false,
             was: '' };
  };

  const addLine = (day) => {
    const lines = linesOf(day);
    const last = lines[lines.length - 1];
    const start = (last && (last.end || last.start))
      || (event.date || '').slice(11, 16) || '09:00';
    setRows((prev) => [...prev, blankRow(day, start, start)]);
  };

  /* ★★ An item in BETWEEN. The whole reason the day flows: a keynote at half eleven does not
     replace game 3, it pushes it — and everything under it — half an hour later, the competition
     included. It lands unsaved, so the hours can still be argued with before anything is written. */
  const insertAt = (day, index) => {
    const lines = linesOf(day);
    const before = lines[index - 1];
    const after = lines[index];
    const start = (before && (before.end || before.start)) || (after && after.start)
      || (event.date || '').slice(11, 16) || '09:00';
    const from = toMin(start);
    const fresh = blankRow(day, start,
                           from === null ? start : toHHMM(clamp(from + INSERT_MINUTES)));
    setRows((prev) => (flow && from !== null
      ? [...shiftDay(prev, day, from, INSERT_MINUTES), fresh]
      : [...prev, fresh]));
  };

  const removeLine = (row) => {
    const from = toMin(row.start);
    const length = toMin(row.end) === null || from === null
      ? 0 : Math.max(0, toMin(row.end) - from);
    setRows((prev) => {
      const left = prev.filter((r) => r.key !== row.key);
      return flow && from !== null ? shiftDay(left, row.day, from, -length) : left;
    });
    if (row.id) setGone((g) => [...g, { id: row.id, day: row.day }]);
  };

  /* Copy a day's programme onto another one. The lines land unsaved, so the hours can be shifted
     before anything is written. Catalogue slots are left behind on purpose: a copy of one would
     be a plain line wearing the game's name, linked to nothing. */
  const copyDay = (from, to) => {
    const src = linesOf(from).filter((r) => !r.is_activity);
    if (!src.length) return;
    setRows((prev) => [...prev, ...src.map((r) => {
      const fresh = blankRow(to, r.start, r.end);
      return { ...fresh, title: r.title, is_challenge: r.is_challenge };
    })]);
    setNotice({ where: to, text: t('copiedLines', { n: src.length }) });
  };

  /* ★★ The computed day, written onto this day of the run sheet — once, as a starting point.

     The rows are built HERE rather than on the server precisely because the words are here:
     ``runLabel`` reads them out of the translation files in the organiser's own language, and a
     run sheet seeded from the server would be in English on a Dutch day. What the server owns is
     which lines are playing moments, and that it happens only once per day. */
  const rowsToWrite = useCallback((day) => planFor(day)
    .filter((row) => row.start)
    .map((row) => ({
      title: runLabel(t, row, comp && comp.break_follows_game),
      start: at(day, row.start),
      end: endAt(day, row.start, row.end || row.start),
      // A 'game' row on a rotation, a 'block' on a knockout or a round robin — both are when the
      // competition is PLAYED, and everything else on the sheet is the programme around it.
      is_challenge: !!row.is_challenge,
      // ★★ The group letter, and it is load-bearing on a multi-group day: the groups take turns,
      // so one playing moment is one line per group, and ``generate_planning_for_event`` reads
      // the letter off the line. Stamping 'A' on all of them — which is what this screen did —
      // left group B with no games at all.
      group: row.group,
    })), [planFor, comp, t]);

  const seedDay = (day) => {
    if (busy || !comp) return;
    const rowsForDay = rowsToWrite(day);
    if (!rowsForDay.length) { setNotice({ where: day, text: t('tournamentRunNoStart') }); return; }
    setBusy(true);
    setNotice(null);
    apiRequest('POST', `/company/event/${eventId}/tournament/runsheet`,
               { day, rows: rowsForDay })
      .then(() => load())
      .catch((err) => setNotice({ where: day, text: apiErrorText(t, err) }))
      .finally(() => setBusy(false));
  };

  /* ★ Every empty day in one press. One request, not one per day: the route checks every named
     day BEFORE it writes anything, so a competition is never half written down. */
  const seedAllDays = () => {
    if (busy || !comp) return;
    const batch = days
      .filter((d) => linesOf(d).length === 0)
      .map((d) => ({ day: d, rows: rowsToWrite(d) }))
      .filter((d) => d.rows.length);
    if (!batch.length) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', `/company/event/${eventId}/tournament/runsheet`, { days: batch })
      .then(() => load())
      .catch((err) => setNotice({ where: batch[0].day, text: apiErrorText(t, err) }))
      .finally(() => setBusy(false));
  };

  /* ★★ One day's schedule back down, the programme left standing.

     Not a variant of "take the schedule down" — that one drops the whole event, its logical teams
     and its scores, and is the right answer only when starting over. This drops what was BUILT on
     one day's lines and nothing else, so the other days go on being played while day three is
     reshaped. The server refuses it the moment anything on that day has been scored. */
  const takeDayDown = (day) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('DELETE', `/company/event/${eventId}/tournament/schedule?day=${day}`)
      .then(() => load())
      .then(() => setNotice({ where: day, text: t('runSheetDayTakenDown') }))
      .catch((err) => setNotice({ where: day, text: apiErrorText(t, err) }))
      .finally(() => setBusy(false));
  };

  /* --- saving --------------------------------------------------------------------------- */

  const saveDay = async (day) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    // ★ ``ofDay``, not ``r.day === day``: a line moved off this day is saved WITH this day, or
    // the move is silently lost the moment it leaves the card you are looking at.
    const dirty = rows.filter((r) => ofDay(r, day) && isDirty(r));
    const payload = dirty.map((r) => {
      const c = compose(r);
      const body = { key: r.key, end: c.end };
      if (r.id) body.id = r.id;
      // An empty hour on a line that already exists leaves its start alone rather than asking
      // the server to clear it, which it refuses. A NEW line has to carry one.
      // ⚠️ A MOVED line must always carry its start: the server parses ``end`` against the day of
      // the start it is holding, so sending the end alone would compose it on the OLD day.
      if (c.start || !r.id || r.movedFrom) body.start = c.start;
      // ★ Never send the title of a catalogue slot: the API reads it as a rename and the line
      // stops being the game it is.
      if (!r.is_activity) body.title = c.title;
      // ★ Only when it MOVED, and this matters more than it used to: un-ticking a line now DROPS
      // its heats. Sending the tick on every save would make the server re-decide a playing moment
      // on a line whose hours were merely nudged — and on a line that was already off, an
      // unrelated edit would quietly take a schedule down.
      if (!r.id || c.is_challenge !== (r.was.split('|')[3] === '1')) {
        body.is_challenge = c.is_challenge;
      }
      return body;
    });
    try {
      const res = await apiRequest('PUT', `/company/event/${eventId}/planning`, {
        rows: payload,
        deleted: gone.filter((g) => g.day === day).map((g) => g.id),
      });
      // ★ Only this day is re-adopted from the answer. The other days may be full of unsaved
      // lines, and a whole-sheet re-adopt would quietly throw them away.
      //
      // ★★ Plus every row this press actually WROTE, wherever it landed. A line moved to another
      // day is saved here and lives there, so adopting "this day" alone would leave the stale
      // copy on screen — permanently dirty, and re-sent on the next Save. ``ids`` is the answer's
      // map of request key to row id, and it exists for exactly this case.
      const written = new Set(Object.values(res.ids || {}).map((id) => String(id).toLowerCase()));
      const savedKeys = new Set(dirty.map((r) => r.key));
      const fresh = rowsOf(res.planning, firstDay)
        .filter((r) => r.day === day || written.has(String(r.id).toLowerCase()));
      setRows((prev) => [
        ...prev.filter((r) => r.day !== day && !savedKeys.has(r.key)),
        ...fresh,
      ]);
      setGone((prev) => prev.filter((g) => g.day !== day));
      // What the competition looks like now: a moment that moved took its heats with it.
      setData((d) => ({ ...d, planning: res.planning, competition: res.competition }));
      setNotice({ where: day, text: t('saved') });
    } catch (err) {
      // Nothing was written — the day on screen is still the day to fix.
      setNotice({ where: day, text: apiErrorText(t, err) });
    } finally {
      setBusy(false);
    }
  };

  /* Stretch the event over more days — the admin's, because it changes what the event IS, and
     what makes this screen worth opening for a programme that does not exist yet.

     ★ It MAILS the people who registered, like every other change to the date does. The tick-box
     is the same one the event editor carries, and for the same reason. */
  const saveSpan = () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    // Keep the hour the event already ends at; a day picker must not silently move it to
    // midnight. A first end date borrows the hour the event starts at.
    const hhmm = (event.end_date || event.date || '').slice(11, 16) || '18:00';
    apiRequest('PUT', `/company/event/${eventId}`,
               { end_date: lastDay ? `${lastDay}T${hhmm}` : '', notify })
      .then((res) => {
        setData((d) => ({ ...d, event: res.event }));
        setLastDay((res.event.end_date || '').slice(0, 10));
        setNotice({ where: 'event',
                    text: res.told ? t('toldRegistered', { n: res.told }) : t('saved') });
      })
      .catch((err) => setNotice({ where: 'event', text: apiErrorText(t, err) }))
      .finally(() => setBusy(false));
  };

  if (data === null) {
    // ★ The arrow is drawn while it loads too. This screen's read is the heaviest on the platform
    // — the whole sheet, the plan and every heat on it — and a spinner with no way out of it is
    // the one state where somebody is most likely to want one.
    return (
      <Base activeNav="home">
        <Card>
          <Back to={`/host-event/${eventId}`} />
          <span className="auth__spinner" />
        </Card>
      </Base>
    );
  }

  const spanChanged = lastDay !== (event.end_date || '').slice(0, 10);
  const heatsOf = (row) => ((comp && comp.heats) || {})[row.id] || [];

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/host-event/${eventId}`} />
        <Title my="1" className="mt-3">
          <Icon name="calendar" theme="primary" className="me-2" />
          {t('runSheetTitle')}
        </Title>
        <p className="bravos-q__hint mb-2">{event.name}</p>
        <p className="ce-note">{t('multiDayRunSheetHint')}</p>

        {/* ★★ The day flows. One tick, because it changes what every other control on this screen
            does — and because the surgical edit during an event is a real and different need. */}
        <label className="ce-check">
          <input type="checkbox" checked={flow} onChange={(e) => setFlow(e.target.checked)} />
          <span>
            {t('runSheetFlow')}
            <span className="ce-check__hint">{t('runSheetFlowHint')}</span>
          </span>
        </label>

        {/* The programme and the competition are the two halves of what happens on the day, so
            the door to the other one belongs here too: writing the run sheet of a tournament is
            exactly when you want to see — or set — what kind of competition it is. No gate of its
            own: reading the setup is a host power, and the screen itself only lets the company
            change it. */}
        <button
          type="button"
          className="bravos-submit bravos-submit--ghost"
          onClick={() => navigate(`/company-event/${eventId}/tournament`)}
        >
          <Icon name="flag-checkered" /> {t('tournamentTitle')}
        </button>

        {/* ★ On paper, with the heats under each line — the half of this screen that a phone held
            in a field is the worst place to read. Every day of the event, because this screen is
            every day of the event; the API takes a `day` for the callers that want one. */}
        <div className="ce-actions mt-2">
          <button type="button" className="btn btn-outline-secondary btn-sm"
                  onClick={() => downloadApiFile(
                    `/company/event/${eventId}/print/run-sheet.pdf`, { lang: i18n.language })}>
            <Icon name="print" className="me-1" />{t('printRunSheetButton')}
          </button>
        </div>
      </Card>

      {/* ---- WHAT IS BEING PLAYED ----
          ★★ Without this the sheet is a list of hours on a day whose shape lives on another
          screen. One line says what the competition is and whether the sheet holds enough playing
          moments for it; behind the fold is the day the plan computes and the field drawn out.
          Both are the SERVER's — the same numbers and the same drawing the cockpit shows, never a
          second opinion worked out here. */}
      {comp && (
        <Card>
          <Title my="1">
            <Icon name="flag-checkered" theme="primary" className="me-2" />
            {t('runSheetCompetition')}
          </Title>
          <p className="ct-sum">
            {t(`tournamentFormat_${comp.format}`)}
            {/* The plan says nothing about moments until the day has been sized, and "3 of 0" is
                worse than silence. */}
            {comp.needed > 0
              && ` · ${t('runSheetMoments', { moments: comp.moments, needed: comp.needed })}`}
          </p>
          {/* The gap that makes a day play short. The generator does not complain about it: it
              builds as many rounds as there are ticked moments and reports success. */}
          {comp.needed > 0 && comp.moments !== comp.needed && (
            <p className="ce-note">
              {t('tournamentMomentsMismatch', { moments: comp.moments, needed: comp.needed })}
            </p>
          )}
          {/* Something has been scored on this day. The hours are still the host's — the heats
              move with their line — but the schedule underneath cannot be rebuilt any more, so a
              moment that is missing here stays missing. */}
          {comp.scored && <p className="ce-note">{t('runSheetScored')}</p>}

          <button type="button" className="ct-explain__toggle"
                  onClick={() => setShape((v) => !v)}>
            <Icon name={shape ? 'angle-left' : 'angle-right'} className="me-1" />
            {t('tournamentHowWillItLook')}
          </button>
          {shape && (
            <div className="ct-run">
              {(comp.schedule || []).length > 0 ? (
                <ol className="ct-run__list">
                  {comp.schedule.map((row, i) => (
                    <li key={`${row.kind}-${row.index || 0}-${i}`}
                        className={`ct-run__row ct-run__row--${row.kind}`}>
                      <span className="ct-run__clock">
                        {row.start ? `${row.start} – ${row.end}` : '—'}
                      </span>
                      <span className="ct-run__what">
                        {runLabel(t, row, comp.break_follows_game)}
                      </span>
                      <span className="ct-run__min">
                        {`${row.minutes} ${t('dim_unitMinutes')}`}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="ct-field__hint">{t('tournamentRunEmpty')}</p>
              )}
              <p className="ct-field__hint">{t('tournamentRunHint')}</p>
              <PlanGrid grid={comp.grid} />
            </div>
          )}
        </Card>
      )}

      {/* No day to draw at all — an event without a date. Nothing to say but so. */}
      {days.length === 0 && <Card><p className="ce-note">{t('noRunSheet')}</p></Card>}

      {days.map((day) => {
        const lines = linesOf(day);
        const sources = days.filter((d) => d !== day
          && linesOf(d).some((r) => !r.is_activity));
        return (
          <Card key={day}>
            <div className="hv-day__head">
              <span className="hv-day__name">{shortDate(day, i18n.language) || day}</span>
              <span className="ce-tab__n">{lines.length}</span>
              {dayDirty(day) && <span className="ce-pill ce-pill--draft">{t('unsavedDay')}</span>}
              {sources.length > 0 && (
                <select
                  className="bravos-network__labselect"
                  value=""
                  onChange={(e) => { if (e.target.value) copyDay(e.target.value, day); }}
                >
                  <option value="">{t('copyFromDay')}</option>
                  {sources.map((d) => (
                    <option key={d} value={d}>{shortDate(d, i18n.language) || d}</option>
                  ))}
                </select>
              )}
            </div>

            {/* ★★ How long THIS day is, and it only appears when there is more than one — a
                one-day event has said this once already, in the dimensions. The split fills each
                day to its own end and continues on the next, so these two boxes are what decides
                that day two starts at "Game 7". Screen state: once the day is written down its
                lines ARE its window, and there is nothing left to remember. */}
            {canManage && comp && days.length > 1 && lines.length === 0 && (
              <div className="hv-day__window">
                <label className="bravos-form__field">
                  <span>{t('runSheetDayStart')}</span>
                  <input type="time" value={windowOf(day).start} disabled={busy}
                         onChange={(e) => setWindow(day, { start: e.target.value })} />
                </label>
                <label className="bravos-form__field">
                  <span>{t('runSheetDayEnd')}</span>
                  <input type="time" value={windowOf(day).end} disabled={busy}
                         onChange={(e) => setWindow(day, { end: e.target.value })} />
                </label>
                {planFor(day).length > 0 && (
                  <p className="ct-field__hint">
                    {t('runSheetDayHolds', { n: (dayPlan(day) || {}).blocks || 0 })}
                  </p>
                )}
              </div>
            )}

            {lines.length === 0 && <p className="ce-note">{t('noRunSheet')}</p>}

            {/* ★★ From the picture of the day to the day itself. An empty day of a competition is
                the one moment this can be offered: it writes the computed programme down as real
                lines with the playing moments ticked, and from then on the sheet is the truth and
                everything below edits it — which is how a keynote ends up between two games.

                ★ Only while THIS day is empty. A starting point, not a merge: a second press would
                lay a second copy of the day over the first and nothing could tell them apart. */}
            {comp && canManage && lines.length === 0 && planFor(day).length > 0 && (
              <div className="ce-actions">
                <button type="button" className="bravos-submit" disabled={busy}
                        onClick={() => seedDay(day)}>
                  <Icon name="calendar" /> {t('runSheetSeedDay')}
                </button>
                <p className="ct-field__hint">{t('tournamentSeedRunSheetHint')}</p>
              </div>
            )}

            {lines.map((r, index) => (
              <React.Fragment key={r.key}>
                {/* Where an item goes in BETWEEN. A thin line with a plus on it, before every
                    row — the end of the day is served by "Add an item" below. */}
                <button
                  type="button"
                  className="hv-ins"
                  title={t('runSheetInsertHere')}
                  aria-label={t('runSheetInsertHere')}
                  disabled={busy}
                  onClick={() => insertAt(day, index)}
                >
                  <span className="hv-ins__plus"><Icon name="plus" /></span>
                </button>
                <div className={`hv-line${r.is_challenge ? ' hv-line--play' : ''}`}>
                  <input
                    type="time"
                    className="hv-line__time"
                    aria-label={t('startTime')}
                    value={r.start}
                    onChange={(e) => setStart(r, e.target.value)}
                  />
                  <input
                    type="time"
                    className="hv-line__time"
                    aria-label={t('endTime')}
                    value={r.end}
                    onChange={(e) => setEnd(r, e.target.value)}
                  />
                  <input
                    type="text"
                    className="hv-line__title"
                    maxLength={100}
                    readOnly={r.is_activity}
                    placeholder={t('runSheetItemPlaceholder')}
                    value={r.title}
                    onChange={(e) => patch(r.key, { title: e.target.value })}
                  />
                  {/* Whose turn this line is. Only drawn where it means something — with one
                      group every line says 'A' and the letter is noise. */}
                  {(comp && comp.groups > 1 && r.is_challenge && r.group) && (
                    <span className="hv-line__group">{r.group}</span>
                  )}
                  {/* ★★ To another day. A line has no day of its own — it has a START — so this
                      rewrites the date part of its hours and nothing else. The heats follow
                      (``sync_heat_times``), so a moment that has already been built travels with
                      its games, and the moment numbers renumber themselves along the clock.
                      Which is the whole answer to "split one master planning over several days":
                      it is a property of WHEN a moment sits, not of the schedule. */}
                  {days.length > 1 && (
                    <select
                      className="hv-line__day"
                      aria-label={t('runSheetMoveToDay')}
                      title={t('runSheetMoveToDay')}
                      value={r.day}
                      disabled={busy || r.is_played}
                      onChange={(e) => moveLine(r, e.target.value)}
                    >
                      {days.map((d) => (
                        <option key={d} value={d}>{shortDate(d, i18n.language) || d}</option>
                      ))}
                    </select>
                  )}
                  {/* ★★ Is the competition PLAYED in this moment? Machina's planning screen has had
                      exactly this tick for as long as there has been a schedule, and it is the one
                      thing the generator reads: a run sheet with none of them ticked builds a day
                      with NO heats in it and says nothing.

                      ★★ What closes the tick is ``is_played``, never ``is_generated``. The
                      schedule is generated over EVERY day in one press, so "there is a plan on this
                      line" is true of day three from the moment day one is built — and locking on
                      it meant a competition could never be reshaped once it had been built once.
                      A line that has been SCORED is the one that cannot be undone: its heats are
                      the only place that result lives. Everything else lets its heats go with it. */}
                  <label
                    className={`hv-line__play${r.is_challenge ? ' is-on' : ''}`}
                    title={r.is_played ? t('runSheetPlayLocked')
                      : (r.is_generated ? t('runSheetPlayRebuilds') : t('runSheetPlayHint'))}
                  >
                    <input
                      type="checkbox"
                      checked={!!r.is_challenge}
                      disabled={busy || r.is_played}
                      onChange={(e) => patch(r.key, { is_challenge: e.target.checked })}
                    />
                    <span>
                      {r.is_challenge && r.sport_order
                        ? t('runSheetPlayN', { n: r.sport_order })
                        : t('runSheetPlay')}
                    </span>
                  </label>
                  <button
                    type="button"
                    className="hv-line__del"
                    aria-label={t('delete')}
                    title={r.is_played ? t('runSheetPlayLocked') : undefined}
                    disabled={busy || r.is_activity || r.is_played}
                    onClick={() => removeLine(r)}
                  >
                    <Icon name="trash" />
                  </button>
                </div>
                {/* ★★ WHAT IS PLAYED IN IT. The heats hanging off this very line, with the team
                    numbers the team sheets carry — the answer to "what is going on at half two",
                    which no run sheet on this platform could give before. Until the schedule is
                    built there is nothing to draw but which moment of how many it is. */}
                {r.is_challenge && comp && (
                  <div className="hv-heats">
                    {heatsOf(r).length > 0 ? heatsOf(r).map((heat) => (
                      <span className="hv-heat" key={heat.id}>
                        {heat.group && <b className="hv-heat__group">{heat.group}</b>}
                        <span className="hv-heat__game">{heat.acronym || heat.game || '—'}</span>
                        {heat.teams.map((number) => (
                          <span className="hv-heat__num" key={number}>{number}</span>
                        ))}
                        {heat.more > 0 && (
                          <span className="hv-heat__more">
                            {t('tournamentGridMore', { n: heat.more })}
                          </span>
                        )}
                      </span>
                    )) : (
                      <span className="hv-heats__none">
                        {r.sport_order && comp.needed
                          ? t('runSheetMomentOf', { n: r.sport_order, total: comp.needed })
                          : t('runSheetNotBuilt')}
                      </span>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
            {lines.some((r) => r.is_played) && (
              <p className="ce-note">{t('runSheetPlayLocked')}</p>
            )}
            {lines.some((r) => r.is_generated && !r.is_played) && (
              <p className="ce-note">{t('runSheetPlayRebuilds')}</p>
            )}
            {lines.some((r) => r.is_generated) && flow && (
              <p className="ce-note">{t('runSheetHeatsFollow')}</p>
            )}
            {dayOverflows(day) && <p className="ce-note">{t('runSheetDayFull')}</p>}

            <div className="bravos-form__row">
              <button
                type="button"
                className="bravos-submit bravos-submit--ghost"
                disabled={busy}
                onClick={() => addLine(day)}
              >
                <Icon name="plus" /> {t('addRunSheetItem')}
              </button>
              <button
                type="button"
                className="bravos-submit"
                disabled={busy || !dayDirty(day)}
                onClick={() => saveDay(day)}
              >
                <Icon name="check" /> {t('saveDay')}
              </button>
              {/* ★★ One DAY of the schedule, back down. The rotation is generated over every day
                  in a single press, so without this the only way to reshape day three is to throw
                  day one away with it — scores and all. The programme stays: the hours, the
                  breaks and the names are what the organiser wrote, and they are what the day
                  gets rebuilt from. Gone as soon as anything on the day has been scored, because
                  then those games are the only place the result lives. */}
              {canManage && lines.some((r) => r.is_generated)
                && !lines.some((r) => r.is_played) && (
                <button
                  type="button"
                  className="bravos-submit bravos-submit--ghost"
                  title={t('runSheetRemoveDayScheduleHint')}
                  disabled={busy}
                  onClick={() => takeDayDown(day)}
                >
                  <Icon name="trash" /> {t('runSheetRemoveDaySchedule')}
                </button>
              )}
            </div>
            {notice && notice.where === day && (
              <p className="bravos-cal__notice">{notice.text}</p>
            )}
          </Card>
        );
      })}

      {/* ---- THE DAYS THIS EVENT RUNS OVER ----
          ★★ Underneath the sheet, on purpose. What this screen is opened for is writing a
          programme; how many days that programme spans is settled once and then never looked at
          again, and above the days it made every visit begin with the administration of the event
          instead of with the day itself. It reads bottom-up too: each day's window is set in its
          own card, and the press that writes every empty one down comes after them all.
          ★ One gate for the section — every control in it is the admin's. */}
      {canManage && (
        <Card>
          <Title my="1">
            <Icon name="calendar" theme="primary" className="me-2" />
            {t('multiDayRunSheet')}
          </Title>
          <div className="bravos-form">
            <label className="bravos-form__label">
              {t('lastDay')}
              <input
                type="date"
                value={lastDay}
                min={firstDay || undefined}
                onChange={(e) => setLastDay(e.target.value)}
              />
            </label>
            <p className="ce-note">{t('lastDayHint')}</p>
            <label className="ce-check">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
              <span>
                {t('notifyOurPeople')}
                <span className="ce-check__hint">{t('notifyOurPeopleHint')}</span>
              </span>
            </label>
            <button
              type="button"
              className="bravos-submit"
              disabled={busy || !spanChanged}
              onClick={saveSpan}
            >
              {t('saveDays')}
            </button>

            {/* ★★ A day that is not next to the others. "Last day" walks a span and can only make
                consecutive days; a competition played on four Thursdays is four dates nobody can
                reach that way. Nothing is stored by adding one — it opens an empty card, exactly
                as Machina's own "Add Day" does, and the day becomes real when it carries a line.
                ⚠️ A day past the event's own end is offered to the span above, because otherwise
                the event's dates quietly disagree with its programme. */}
            <label className="bravos-form__label">
              {t('runSheetAddDay')}
              <input
                type="date"
                value={newDay}
                min={firstDay || undefined}
                onChange={(e) => setNewDay(e.target.value)}
              />
            </label>
            <p className="ce-note">{t('runSheetAddDayHint')}</p>
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              disabled={busy || !newDay || days.includes(newDay)}
              onClick={() => {
                setExtraDays((prev) => (prev.includes(newDay) ? prev : [...prev, newDay]));
                if (newDay > (lastDay || firstDay)) setLastDay(newDay);
                setNewDay('');
              }}
            >
              <Icon name="plus" /> {t('runSheetAddDay')}
            </button>
          </div>

          {/* ★ Every empty day from the plan, in one press and one request — the route
              checks all of them before it writes any, so a competition is never half
              written down. */}
          {comp && days.length > 1
            && days.some((d) => linesOf(d).length === 0 && planFor(d).length > 0) && (
            <div className="ce-actions">
              <button type="button" className="bravos-submit" disabled={busy}
                      onClick={seedAllDays}>
                <Icon name="calendar" /> {t('runSheetSeedAllDays')}
              </button>
              <p className="ct-field__hint">{t('runSheetSeedAllDaysHint')}</p>
              {plan && plan.unplaced > 0 && (
                <p className="ce-note ce-note--warn">
                  {t('tournamentNote_days_short')}
                </p>
              )}
            </div>
          )}
          {notice && notice.where === 'event' && (
            <p className="bravos-cal__notice">{notice.text}</p>
          )}
        </Card>
      )}
    </Base>
  );
};

export default HostRunSheet;
